const TRUE_VALUES = new Set(["1", "true", "yes", "on"]);
const FALSE_VALUES = new Set(["0", "false", "no", "off"]);
const SESSION_ID_STORAGE_KEY = "ea.performanceTelemetry.sessionId";
const DEFAULT_SAMPLE_RATE = 0.05;
const ADMIN_SAMPLE_RATE = 0.25;
const MAX_BUFFER_SIZE = 80;
const MAX_SEND_BATCH_SIZE = 20;
const FLUSH_DELAY_MS = 2500;
const AFTER_PAINT_DELAY_MS = 250;
const MAX_ROUTE_MILESTONE_VALUE_MS = 5 * 60 * 1000;

const ALLOWED_METRIC_NAMES = new Set([
  "CLS",
  "FCP",
  "INP",
  "LCP",
  "TTFB",
  "route_after_paint",
  "route_shell_ready",
  "route_data_ready",
  "route_interaction_ready",
  "route_error",
]);

const ROUTE_MILESTONE_METRICS = new Set([
  "route_after_paint",
  "route_shell_ready",
  "route_data_ready",
  "route_interaction_ready",
]);

const clampRate = (value, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(1, Math.max(0, numeric));
};

const safeString = (value, maxLength = 160) =>
  typeof value === "string" ? value.trim().slice(0, maxLength) : "";

const normalizeFlag = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  if (value == null) return null;
  const normalized = String(value).trim().toLowerCase();
  if (TRUE_VALUES.has(normalized)) return true;
  if (FALSE_VALUES.has(normalized)) return false;
  return null;
};

const readStorage = (storage, key) => {
  if (!storage || typeof storage.getItem !== "function") return "";
  try {
    return storage.getItem(key) || "";
  } catch (_error) {
    return "";
  }
};

const writeStorage = (storage, key, value) => {
  if (!storage || typeof storage.setItem !== "function") return;
  try {
    storage.setItem(key, value);
  } catch (_error) {
    // Telemetry persistence must never affect app behavior.
  }
};

const randomToken = (windowLike = globalThis.window) => {
  const crypto = windowLike?.crypto || globalThis.crypto;
  if (crypto?.getRandomValues) {
    const values = new Uint32Array(2);
    crypto.getRandomValues(values);
    return Array.from(values, (value) => value.toString(36)).join("");
  }
  return Math.random().toString(36).slice(2, 12);
};

const getOrCreateSessionId = (windowLike = globalThis.window) => {
  const stored = readStorage(windowLike?.localStorage, SESSION_ID_STORAGE_KEY);
  if (stored) return stored;

  const generated = `rum-session-${Date.now().toString(36)}-${randomToken(
    windowLike,
  )}`;
  writeStorage(windowLike?.localStorage, SESSION_ID_STORAGE_KEY, generated);
  return generated;
};

const buildTraceId = (windowLike = globalThis.window) => {
  const params = new URLSearchParams(windowLike?.location?.search || "");
  const explicitTrace = params.get("eaProfileTrace");
  if (explicitTrace) return safeString(explicitTrace, 120);
  return `rum-${Date.now().toString(36)}-${randomToken(windowLike).slice(0, 8)}`;
};

export const hashStringToUnitInterval = (value = "") => {
  let hash = 2166136261;
  const text = String(value);
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967296;
};

const isAdminRoute = ({ routePath = "", routeMeta = {} } = {}) =>
  routePath.startsWith("/admin") || Boolean(routeMeta?.requiresAdmin);

const getExplicitSamplingOverride = (search = "") => {
  const params = new URLSearchParams(search || "");
  for (const key of ["eaRum", "eaProfile", "profileRoutes"]) {
    if (!params.has(key)) continue;
    const explicit = normalizeFlag(params.get(key));
    if (explicit !== null) {
      return {
        enabled: explicit,
        reason: key,
      };
    }
  }
  return null;
};

export const shouldSamplePerformanceTelemetry = ({
  search = "",
  routePath = "",
  routeMeta = {},
  sampleSeed = "",
  sampleRate = DEFAULT_SAMPLE_RATE,
  adminSampleRate = ADMIN_SAMPLE_RATE,
  random = Math.random,
} = {}) => {
  const explicitOverride = getExplicitSamplingOverride(search);
  if (explicitOverride) {
    return {
      enabled: explicitOverride.enabled,
      forced: true,
      sampleRate: explicitOverride.enabled ? 1 : 0,
      reason: explicitOverride.reason,
    };
  }

  const effectiveSampleRate = isAdminRoute({ routePath, routeMeta })
    ? clampRate(adminSampleRate, ADMIN_SAMPLE_RATE)
    : clampRate(sampleRate, DEFAULT_SAMPLE_RATE);

  if (effectiveSampleRate <= 0) {
    return {
      enabled: false,
      forced: false,
      sampleRate: effectiveSampleRate,
      reason: "sample_rate_zero",
    };
  }
  if (effectiveSampleRate >= 1) {
    return {
      enabled: true,
      forced: false,
      sampleRate: effectiveSampleRate,
      reason: "sample_rate_full",
    };
  }

  const score = sampleSeed
    ? hashStringToUnitInterval(`${sampleSeed}:${routePath}`)
    : random();

  return {
    enabled: score < effectiveSampleRate,
    forced: false,
    sampleRate: effectiveSampleRate,
    reason: "sampled",
  };
};

const stripQueryAndHash = (value = "") => {
  const [withoutHash] = String(value).split("#");
  const [withoutQuery] = withoutHash.split("?");
  return withoutQuery;
};

const getMatchedRoutePath = (route = {}) => {
  const matched = Array.isArray(route.matched) ? route.matched : [];
  for (let index = matched.length - 1; index >= 0; index -= 1) {
    const path = safeString(matched[index]?.path, 240);
    if (path && path !== "/") return path;
  }
  return "";
};

const routeGroupForPath = ({ routeName = "", routePath = "" } = {}) => {
  if (routePath.includes("/visual-edit/")) return "visualEditor";
  if (routePath.includes("/ai-voice-analytics")) return "analytics";
  if (routePath.startsWith("/admin")) return "adminList";
  if (routePath.includes("/sessions/") && routePath.endsWith("/new")) {
    return "sessionSetup";
  }
  if (routePath.startsWith("/sessions/") && !routePath.endsWith("/new")) {
    return "activeSession";
  }
  if (routePath.startsWith("/knowledge/")) return "questionBankLanding";
  if (String(routeName).toLowerCase().includes("session")) return "session";
  return "simple";
};

export const normalizeRouteForTelemetry = (route = {}) => {
  const matchedPath = getMatchedRoutePath(route);
  const rawPath =
    matchedPath ||
    route.path ||
    route.fullPath ||
    globalThis.window?.location?.pathname ||
    "/";
  let routePath = stripQueryAndHash(rawPath);
  if (!routePath.startsWith("/")) routePath = `/${routePath}`;
  routePath = routePath.replace(/\/{2,}/g, "/").slice(0, 240);

  const routeName = safeString(String(route.name || ""), 120);
  const routeMeta = route.meta || {};

  return {
    routeName,
    routePath,
    routeGroup: routeGroupForPath({ routeName, routePath }),
    isAdmin: isAdminRoute({ routePath, routeMeta }),
  };
};

const getDeviceClass = (windowLike = globalThis.window) => {
  const width = Number(windowLike?.innerWidth);
  if (!Number.isFinite(width)) return "unknown";
  if (width < 600) return "mobile";
  if (width < 960) return "tablet";
  return "desktop";
};

const getConnectionType = (navigatorLike = globalThis.navigator) =>
  safeString(navigatorLike?.connection?.effectiveType || "", 40);

const getAppVersion = () => {
  try {
    return safeString(import.meta.env?.VITE_APP_VERSION || "", 120);
  } catch (_error) {
    return "";
  }
};

const normalizeMetricValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (numeric < 10) return Math.round(Math.max(0, numeric) * 1000) / 1000;
  return Math.round(Math.max(0, numeric));
};

const isStaleRouteMilestone = ({ metricName, value }) =>
  ROUTE_MILESTONE_METRICS.has(metricName) &&
  Number.isFinite(value) &&
  value > MAX_ROUTE_MILESTONE_VALUE_MS;

const sanitizeMetric = (metric = {}, baseContext = {}) => {
  const metricName = safeString(metric.metricName || metric.name, 80);
  if (!ALLOWED_METRIC_NAMES.has(metricName)) return null;

  const value = normalizeMetricValue(metric.value);
  if (value == null) return null;
  if (isStaleRouteMilestone({ metricName, value })) return null;

  const routePath = safeString(
    stripQueryAndHash(metric.routePath || baseContext.routePath || ""),
    240,
  );
  if (!routePath || !routePath.startsWith("/") || routePath.startsWith("//")) {
    return null;
  }

  return {
    metricName,
    value,
    rating: safeString(metric.rating, 40) || "unknown",
    routeName: safeString(metric.routeName || baseContext.routeName, 120),
    routePath,
    routeGroup: safeString(metric.routeGroup || baseContext.routeGroup, 80),
    traceId: safeString(metric.traceId || baseContext.traceId, 120),
    sampleId: safeString(metric.sampleId || baseContext.sampleId, 120),
    appVersion: safeString(metric.appVersion || baseContext.appVersion, 120),
    deviceClass: safeString(metric.deviceClass || baseContext.deviceClass, 40),
    connectionType: safeString(
      metric.connectionType || baseContext.connectionType,
      40,
    ),
  };
};

const sanitizeMetricForContext = (metric = {}, routeContext = {}) =>
  sanitizeMetric(metric, {
    traceId: routeContext.traceId,
    sampleId: routeContext.sampleId,
    routeName: routeContext.routeName,
    routePath: routeContext.routePath,
    routeGroup: routeContext.routeGroup,
    appVersion: routeContext.appVersion,
    deviceClass: routeContext.deviceClass,
    connectionType: routeContext.connectionType,
  });

export const createTelemetryBuffer = ({
  sender,
  flushDelayMs = FLUSH_DELAY_MS,
  batchSize = MAX_SEND_BATCH_SIZE,
  maxBufferSize = MAX_BUFFER_SIZE,
  setTimeout = globalThis.setTimeout,
  clearTimeout = globalThis.clearTimeout,
  console: consoleLike = globalThis.console,
} = {}) => {
  const safeBatchSize = Math.max(1, Math.min(MAX_SEND_BATCH_SIZE, batchSize));
  const queue = [];
  let timer = null;
  let flushing = false;
  let activeFlush = null;

  const scheduleFlush = () => {
    if (timer !== null || typeof setTimeout !== "function") return;
    timer = setTimeout(() => {
      timer = null;
      return flush();
    }, flushDelayMs);
  };

  const flush = async () => {
    if (flushing) return activeFlush;
    if (timer !== null && typeof clearTimeout === "function") {
      clearTimeout(timer);
      timer = null;
    }

    flushing = true;
    activeFlush = (async () => {
      while (queue.length > 0) {
        const metrics = queue.splice(0, safeBatchSize);
        try {
          if (typeof sender === "function") {
            await sender({ metrics });
          }
        } catch (error) {
          if (consoleLike?.debug) {
            consoleLike.debug("[EA performance telemetry] send failed", error);
          }
        }
      }
    })().finally(() => {
      flushing = false;
      activeFlush = null;
    });

    return activeFlush;
  };

  const record = (metric) => {
    if (!metric) return false;
    queue.push(metric);
    while (queue.length > maxBufferSize) queue.shift();
    scheduleFlush();
    return true;
  };

  return {
    record,
    flush,
    get size() {
      return queue.length;
    },
  };
};

const createFirebaseSender = ({
  functions,
  createCallable,
} = {}) => {
  if (!functions && !createCallable) return null;
  let callablePromise = null;
  return async (payload) => {
    if (!callablePromise) {
      callablePromise = Promise.resolve(
        createCallable
          ? createCallable(functions)
          : import("firebase/functions").then(({ httpsCallable }) =>
              httpsCallable(functions, "recordClientPerformanceMetric"),
            ),
      );
    }
    const callable = await callablePromise;
    if (typeof callable === "function") {
      await callable(payload);
    }
  };
};

const scheduleIdle = (windowLike, callback) => {
  if (typeof windowLike?.requestIdleCallback === "function") {
    return windowLike.requestIdleCallback(callback, { timeout: 3000 });
  }
  if (typeof windowLike?.setTimeout === "function") {
    return windowLike.setTimeout(callback, 1000);
  }
  callback();
  return null;
};

const afterPaint = (windowLike, callback) => {
  const raf = windowLike?.requestAnimationFrame;
  const timeout = windowLike?.setTimeout;
  if (typeof raf === "function" && typeof timeout === "function") {
    raf(() => {
      raf(() => {
        timeout(callback, AFTER_PAINT_DELAY_MS);
      });
    });
    return;
  }
  if (typeof timeout === "function") {
    timeout(callback, AFTER_PAINT_DELAY_MS);
    return;
  }
  callback();
};

export const installPerformanceTelemetry = ({
  router,
  functions,
  window: windowLike = globalThis.window,
  navigator: navigatorLike = globalThis.navigator,
  console: consoleLike = globalThis.console,
  createCallable,
  webVitalsLoader = () => import("web-vitals"),
  sampleRate = DEFAULT_SAMPLE_RATE,
  adminSampleRate = ADMIN_SAMPLE_RATE,
} = {}) => {
  if (!router || !windowLike) return null;

  const currentRoute = router.currentRoute?.value || {};
  const samplingSearch = windowLike.location?.search || "";
  const explicitOverride = getExplicitSamplingOverride(samplingSearch);
  if (explicitOverride && !explicitOverride.enabled) {
    windowLike.__EA_PERFORMANCE_TELEMETRY__ = {
      enabled: false,
      sampleRate: 0,
      reason: explicitOverride.reason,
      mark: () => null,
      flush: () => Promise.resolve(),
    };
    return windowLike.__EA_PERFORMANCE_TELEMETRY__;
  }

  const hasResolvedInitialRoute =
    Array.isArray(currentRoute.matched) && currentRoute.matched.length > 0;
  const initialRoute = normalizeRouteForTelemetry(
    hasResolvedInitialRoute
      ? currentRoute
      : { fullPath: windowLike.location?.pathname || "/" },
  );
  const sessionId = getOrCreateSessionId(windowLike);
  const traceId = buildTraceId(windowLike);
  const startedAt = windowLike.performance?.now?.() || Date.now();
  const sender = createFirebaseSender({ functions, createCallable });
  const buffer = createTelemetryBuffer({
    sender,
    setTimeout: windowLike.setTimeout?.bind(windowLike),
    clearTimeout: windowLike.clearTimeout?.bind(windowLike),
    console: consoleLike,
  });

  const sampleForRoute = (routeInfo, routeMeta = {}) =>
    shouldSamplePerformanceTelemetry({
      search: samplingSearch,
      routePath: routeInfo.routePath,
      routeMeta,
      sampleSeed: sessionId,
      sampleRate,
      adminSampleRate,
    });

  const createRouteContext = ({
    routeInfo,
    routeMeta = {},
    routeStartedAt = windowLike.performance?.now?.() || Date.now(),
  }) => ({
    ...routeInfo,
    traceId,
    sample: sampleForRoute(routeInfo, routeMeta),
    sampleId: `route-${Date.now().toString(36)}-${randomToken(windowLike)}`,
    routeStartedAt,
    appVersion: getAppVersion(),
    deviceClass: getDeviceClass(windowLike),
    connectionType: getConnectionType(navigatorLike),
  });

  let currentRouteContext = createRouteContext({
    routeInfo: initialRoute,
    routeMeta: currentRoute.meta || {},
    routeStartedAt: startedAt,
  });
  let pageLoadRouteContext = currentRouteContext;
  let waitingForInitialRouteResolution = !hasResolvedInitialRoute;
  let webVitalsStarted = false;

  const recordForContext = (metric, routeContext = currentRouteContext) => {
    if (!routeContext?.sample?.enabled) return false;
    const sanitized = sanitizeMetricForContext(metric, routeContext);
    if (!sanitized) return false;
    return buffer.record(sanitized);
  };

  const startPageLoadWebVitals = () => {
    if (webVitalsStarted || !pageLoadRouteContext.sample?.enabled) return;
    webVitalsStarted = true;
    scheduleIdle(windowLike, async () => {
      try {
        const {
          onCLS,
          onFCP,
          onINP,
          onLCP,
          onTTFB,
        } = await webVitalsLoader();
        const handleVital = (vital) => {
          recordForContext(
            {
              metricName: vital.name,
              value: vital.value,
              rating: vital.rating,
            },
            pageLoadRouteContext,
          );
        };
        onCLS(handleVital);
        onFCP(handleVital);
        onINP(handleVital);
        onLCP(handleVital);
        onTTFB(handleVital);
      } catch (error) {
        if (consoleLike?.debug) {
          consoleLike.debug(
            "[EA performance telemetry] web-vitals unavailable",
            error,
          );
        }
      }
    });
  };

  const markForContext = (
    metricName,
    data = {},
    routeContext = currentRouteContext,
  ) => {
    if (!routeContext?.sample?.enabled) return false;
    const now = windowLike.performance?.now?.() || Date.now();
    const elapsed = Math.max(0, now - routeContext.routeStartedAt);
    return recordForContext(
      {
        metricName,
        value: elapsed,
        ...data,
      },
      routeContext,
    );
  };

  const createRouteMarker = ({
    route = router.currentRoute?.value || currentRoute,
    routeGroup = "",
    startedAt = windowLike.performance?.now?.() || Date.now(),
  } = {}) => {
    const routeInfo = normalizeRouteForTelemetry(route);
    const routeContext = createRouteContext({
      routeInfo: {
        ...routeInfo,
        routeGroup: safeString(routeGroup, 80) || routeInfo.routeGroup,
      },
      routeMeta: route?.meta || {},
      routeStartedAt: startedAt,
    });

    return (metricName, data = {}) =>
      markForContext(metricName, data, routeContext);
  };

  const api = {
    get enabled() {
      return Boolean(currentRouteContext.sample?.enabled);
    },
    traceId,
    get sampleRate() {
      return currentRouteContext.sample?.sampleRate || 0;
    },
    get currentSampleId() {
      return currentRouteContext.sampleId;
    },
    get routePath() {
      return currentRouteContext.routePath;
    },
    createRouteMarker,
    mark: markForContext,
    record: (metric) => recordForContext(metric),
    flush: buffer.flush,
  };

  windowLike.__EA_PERFORMANCE_TELEMETRY__ = api;

  router.afterEach((to, _from, failure) => {
    if (failure) {
      return;
    }
    currentRouteContext = createRouteContext({
      routeInfo: normalizeRouteForTelemetry(to),
      routeMeta: to?.meta || {},
    });
    if (waitingForInitialRouteResolution) {
      pageLoadRouteContext = currentRouteContext;
      waitingForInitialRouteResolution = false;
      startPageLoadWebVitals();
    }
    const routeContext = currentRouteContext;
    afterPaint(windowLike, () => {
      markForContext("route_after_paint", {}, routeContext);
    });
  });

  router.onError((error, to) => {
    const routeContext = to
      ? createRouteContext({
          routeInfo: normalizeRouteForTelemetry(to),
          routeMeta: to?.meta || {},
        })
      : currentRouteContext;
    markForContext(
      "route_error",
      {
        value: 1,
        rating: "poor",
        error: error?.message || String(error),
      },
      routeContext,
    );
  });

  if (!waitingForInitialRouteResolution) startPageLoadWebVitals();

  windowLike.addEventListener?.("pagehide", () => {
    void buffer.flush();
  });
  windowLike.addEventListener?.("visibilitychange", () => {
    if (globalThis.document?.visibilityState === "hidden") {
      void buffer.flush();
    }
  });

  return api;
};

export const markPerformanceMilestone = (metricName, data = {}) => {
  try {
    return globalThis.window?.__EA_PERFORMANCE_TELEMETRY__?.mark?.(
      metricName,
      data,
    ) || false;
  } catch (_error) {
    return false;
  }
};

export const createPerformanceRouteMarker = (options = {}) => {
  try {
    const marker =
      globalThis.window?.__EA_PERFORMANCE_TELEMETRY__?.createRouteMarker?.(
        options,
      );
    return typeof marker === "function" ? marker : null;
  } catch (_error) {
    return null;
  }
};
